import React, { Component } from 'react';
import Table from '../../tables/Table';
import { orderBy, filter } from 'lodash';
import { dateToWeekdayShort, msToHour, toDisplayString } from '../../../lib/date.utils';
import HighlightRow from '../../tables/HighlightRow';
import SelectRow from '../../tables/SelectRow';
import { hasPermissions, PERMISSIONS } from '../../../constants/authentication/permissions';
import CategoryBadge from '../../CategoryBadge';
import { LIVE_VIEW_TYPE, POST_VIEW_TYPE } from '../../../constants/clearance';

const EMPTY_CELL = <span className="u-truncate">—</span>;

class ListView extends Component {
    _getViewType = () => (this.props.isLiveView ? LIVE_VIEW_TYPE : POST_VIEW_TYPE);

    _getCategoryInfo = () => {
        const viewType = this._getViewType();
        const categoryMap = {};

        this.props.entries.forEach(entry => {
            if (!entry.clearing || typeof entry.clearing !== 'object') return;
            Object.entries(entry.clearing)
                .filter(([, value]) => value?.category?.label && value?.category?.visibleInViews?.includes(viewType))
                .forEach(([key, value]) => {
                    if (!categoryMap[key]) categoryMap[key] = value.category.label;
                });
        });

        return Object.entries(categoryMap).map(([key, label]) => ({ key, label }));
    };

    _getHeaders = () => {
        const categoryHeaders = this._getCategoryInfo().map(({ key, label }) => ({ label, key: `clearing.${key}` }));
        return [
            { label: 'Artist', key: 'artistName' },
            { label: 'Stage', key: 'stageName' },
            { label: 'Performance Date', key: 'date' },
            ...categoryHeaders,
            { label: 'Comment', key: 'clearing.comment' }
        ];
    };

    _getTransformFunctions = () => {
        const transforms = {
            date: (_, data) =>
                `${dateToWeekdayShort(data.stageDate)} ${toDisplayString(data.stageDate)} ${msToHour(data.start)} - ${msToHour(data.end)}`,
            'clearing.comment': (_, data) => <span className="u-truncate">{data.clearing?.comment || '—'}</span>
        };

        this._getCategoryInfo().forEach(({ key }) => {
            transforms[`clearing.${key}`] = (_, data) => {
                const categoryValue = data.clearing?.[key];
                const label = categoryValue?.label?.label;
                if (!label) return EMPTY_CELL;

                return (
                    <CategoryBadge
                        label={label}
                        color={categoryValue.label?.color}
                        untilDate={categoryValue.untilDate}
                        isLiveView={this.props.isLiveView}
                    />
                );
            };
        });

        return transforms;
    };

    _changeSort = field => {
        this.props.setFilter('sortBy', field);
        this.props.setFilter('order', this.props.sortBy === field && this.props.order === 'asc' ? 'desc' : 'asc');
    };

    _filterEntry = entry => {
        const { artistsFilters, stagesFilters, categoryFilters, isLiveView } = this.props;
        const matchesArtist = !artistsFilters?.length || artistsFilters.includes(entry.artistName);
        const matchesStage = !stagesFilters?.length || stagesFilters.includes(entry.stageName);

        // Check category filters
        let matchesCategories = true;
        if (categoryFilters && Object.keys(categoryFilters).length > 0) {
            const viewType = isLiveView ? LIVE_VIEW_TYPE : POST_VIEW_TYPE;
            Object.entries(categoryFilters).forEach(([categoryId, selectedLabels]) => {
                if (selectedLabels && selectedLabels.length > 0) {
                    const categoryValue = entry.clearing?.[categoryId];
                    const isVisibleInView = categoryValue?.category?.visibleInViews?.includes(viewType);
                    const entryLabel = categoryValue?.label?.label || '';

                    if (isVisibleInView) {
                        matchesCategories = matchesCategories && selectedLabels.includes(entryLabel);
                    }
                }
            });
        }

        return matchesArtist && matchesStage && matchesCategories;
    };

    _getEntries = () =>
        orderBy(
            filter(this.props.entries, this._filterEntry),
            this.props.sortBy === 'date'
                ? d => `${toDisplayString(d.stageDate)} ${d.start} - ${d.end}`
                : this.props.sortBy,
            this.props.order
        );
    render() {
        const headers = this._getHeaders();

        return (
            <Table
                selectAllChecked={this.props.selectAllChecked}
                enableSelectFunction={() => true}
                selectAll={this.props.selectAll}
                isSelected={item => (this.props.selectedEntries || []).includes(item.id)}
                onSelect={this.props.onSelect}
                enableRowFunction={() => this.props.isAdmin && hasPermissions(this.props.user, PERMISSIONS.EVENT_EDIT)}
                disableTooltip
                RowComponent={this.props.isAdmin ? SelectRow : HighlightRow}
                highlightFunction={() => ''}
                sort={this.props.sortBy}
                changeSort={this._changeSort}
                sortDirection={this.props.order}
                smallColumns={[]}
                largeColumns={headers.map(h => h.key)}
                transformFunctions={this._getTransformFunctions()}
                canOpenRows
                noSelect={this.props.noSelect}
                onOpen={this.props.showEntry}
                headers={headers}
                data={this._getEntries()}
            />
        );
    }
}

export default ListView;
